/**
 * Viewport state for the diagram canvas: pan, zoom, pinch and fit-to-screen.
 *
 * The transform is stored as `{ x, y, scale }` in *screen* space and applied as
 * `translate(x, y) scale(s)` to a single SVG group. Keeping one authoritative
 * transform (rather than mutating node coordinates) is what lets the diagram be
 * recompiled underneath the user without the view jumping.
 *
 * Wheel handling follows the convention every diagram tool has converged on:
 * a wheel event with `ctrlKey` is a trackpad pinch and zooms about the pointer;
 * everything else is a two-finger scroll and pans. That way a trackpad behaves
 * the way people expect without a modifier, and a mouse wheel still zooms with
 * Ctrl held.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  PointerEvent as ReactPointerEvent,
  RefObject,
  WheelEvent as ReactWheelEvent,
} from 'react';
import type { GraphBounds, Point, Rect } from '../graph/model/types';

export interface Viewport {
  readonly x: number;
  readonly y: number;
  readonly scale: number;
}

export const IDENTITY_VIEWPORT: Viewport = { x: 0, y: 0, scale: 1 };

export const MIN_SCALE = 0.1;
export const MAX_SCALE = 4;

export interface UseViewportOptions {
  readonly containerRef: RefObject<HTMLElement | null>;
  readonly bounds: GraphBounds | null;
  /** Blank space kept around the diagram when fitting. */
  readonly padding?: number;
  /** Disables the ease-in on programmatic moves. */
  readonly reducedMotion?: boolean;
}

export interface ViewportController {
  readonly viewport: Viewport;
  readonly isPanning: boolean;
  setViewport(next: Viewport): void;
  zoomBy(factor: number, origin?: Point): void;
  zoomIn(): void;
  zoomOut(): void;
  resetZoom(): void;
  fitToScreen(): void;
  /** Brings a graph-space rectangle into view, centred, without changing scale. */
  centerOn(rect: Rect): void;
  /** Fits a graph-space rectangle, adjusting scale if it does not already fit. */
  revealRect(rect: Rect): void;
  /** Screen coordinates -> graph coordinates. */
  toGraphPoint(screen: Point): Point;
  readonly handlers: {
    onPointerDown(event: ReactPointerEvent<Element>): void;
    onPointerMove(event: ReactPointerEvent<Element>): void;
    onPointerUp(event: ReactPointerEvent<Element>): void;
    onPointerCancel(event: ReactPointerEvent<Element>): void;
    onWheel(event: ReactWheelEvent<Element>): void;
  };
}

interface ActivePointer {
  readonly id: number;
  x: number;
  y: number;
}

function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/**
 * Pointer capture is a nice-to-have, not a requirement.
 *
 * `setPointerCapture` throws `NotFoundError` when the id is not an active
 * pointer, which happens with synthetic events and with pointers the browser
 * has already cancelled. Losing capture only means a drag may end early if the
 * cursor leaves the element — letting the exception through would break the
 * gesture entirely.
 */
function capturePointer(element: Element, pointerId: number, capture: boolean): void {
  try {
    if (capture) element.setPointerCapture?.(pointerId);
    else element.releasePointerCapture?.(pointerId);
  } catch {
    // Not capturable; carry on with plain move events.
  }
}

export function useViewport(options: UseViewportOptions): ViewportController {
  const { containerRef, bounds } = options;
  const padding = options.padding ?? 48;

  const [viewport, setViewportState] = useState<Viewport>(IDENTITY_VIEWPORT);
  const [isPanning, setIsPanning] = useState(false);

  // Pointer bookkeeping lives in refs: it changes on every move event and must
  // not drive re-renders.
  const pointers = useRef(new Map<number, ActivePointer>());
  const panOrigin = useRef<{ x: number; y: number; viewport: Viewport } | null>(null);
  const pinchOrigin = useRef<{ distance: number; centre: Point; viewport: Viewport } | null>(null);
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;

  const setViewport = useCallback((next: Viewport) => {
    setViewportState({ ...next, scale: clampScale(next.scale) });
  }, []);

  const containerRect = useCallback((): DOMRect | null => {
    const element = containerRef.current;
    return element ? element.getBoundingClientRect() : null;
  }, [containerRef]);

  const toGraphPoint = useCallback((screen: Point): Point => {
    const current = viewportRef.current;
    return {
      x: (screen.x - current.x) / current.scale,
      y: (screen.y - current.y) / current.scale,
    };
  }, []);

  /** Zooms about a fixed screen point, so the pixel under the cursor stays put. */
  const zoomAbout = useCallback((factor: number, origin: Point) => {
    const current = viewportRef.current;
    const scale = clampScale(current.scale * factor);
    if (scale === current.scale) return;
    const ratio = scale / current.scale;
    setViewportState({
      scale,
      x: origin.x - (origin.x - current.x) * ratio,
      y: origin.y - (origin.y - current.y) * ratio,
    });
  }, []);

  const zoomBy = useCallback(
    (factor: number, origin?: Point) => {
      const rect = containerRect();
      const centre = origin ?? (rect ? { x: rect.width / 2, y: rect.height / 2 } : { x: 0, y: 0 });
      zoomAbout(factor, centre);
    },
    [containerRect, zoomAbout],
  );

  const zoomIn = useCallback(() => zoomBy(1.25), [zoomBy]);
  const zoomOut = useCallback(() => zoomBy(1 / 1.25), [zoomBy]);

  const fitToScreen = useCallback(() => {
    const rect = containerRect();
    if (!rect || !bounds || bounds.width <= 0 || bounds.height <= 0) {
      setViewportState(IDENTITY_VIEWPORT);
      return;
    }
    const scale = clampScale(
      Math.min(
        (rect.width - padding * 2) / bounds.width,
        (rect.height - padding * 2) / bounds.height,
        // Never blow a tiny diagram up to fill the screen — it looks broken.
        1.5,
      ),
    );
    setViewportState({
      scale,
      x: rect.width / 2 - (bounds.minX + bounds.width / 2) * scale,
      y: rect.height / 2 - (bounds.minY + bounds.height / 2) * scale,
    });
  }, [bounds, containerRect, padding]);

  const resetZoom = useCallback(() => {
    const rect = containerRect();
    if (!rect || !bounds) {
      setViewportState(IDENTITY_VIEWPORT);
      return;
    }
    setViewportState({
      scale: 1,
      x: rect.width / 2 - (bounds.minX + bounds.width / 2),
      y: rect.height / 2 - (bounds.minY + bounds.height / 2),
    });
  }, [bounds, containerRect]);

  const centerOn = useCallback(
    (target: Rect) => {
      const rect = containerRect();
      if (!rect) return;
      const current = viewportRef.current;
      setViewportState({
        scale: current.scale,
        x: rect.width / 2 - (target.x + target.width / 2) * current.scale,
        y: rect.height / 2 - (target.y + target.height / 2) * current.scale,
      });
    },
    [containerRect],
  );

  const revealRect = useCallback(
    (target: Rect) => {
      const rect = containerRect();
      if (!rect) return;
      const current = viewportRef.current;
      const margin = 64;
      const fits =
        target.width * current.scale < rect.width - margin &&
        target.height * current.scale < rect.height - margin;
      const scale = fits
        ? current.scale
        : clampScale(
            Math.min(
              (rect.width - margin * 2) / Math.max(1, target.width),
              (rect.height - margin * 2) / Math.max(1, target.height),
            ),
          );
      setViewportState({
        scale,
        x: rect.width / 2 - (target.x + target.width / 2) * scale,
        y: rect.height / 2 - (target.y + target.height / 2) * scale,
      });
    },
    [containerRect],
  );

  /* --- pointer interaction ------------------------------------------- */

  const localPoint = useCallback(
    (event: { clientX: number; clientY: number }): Point => {
      const rect = containerRect();
      if (!rect) return { x: event.clientX, y: event.clientY };
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    },
    [containerRect],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<Element>) => {
      // Only the primary button pans; right-click is left for a context menu.
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const point = localPoint(event);
      pointers.current.set(event.pointerId, { id: event.pointerId, ...point });
      capturePointer(event.currentTarget as Element, event.pointerId, true);

      if (pointers.current.size === 1) {
        panOrigin.current = { ...point, viewport: viewportRef.current };
        pinchOrigin.current = null;
        setIsPanning(true);
      } else if (pointers.current.size === 2) {
        const [a, b] = [...pointers.current.values()];
        if (a && b) {
          pinchOrigin.current = {
            distance: Math.hypot(a.x - b.x, a.y - b.y),
            centre: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
            viewport: viewportRef.current,
          };
          panOrigin.current = null;
        }
      }
    },
    [localPoint],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<Element>) => {
      const tracked = pointers.current.get(event.pointerId);
      if (!tracked) return;
      const point = localPoint(event);
      tracked.x = point.x;
      tracked.y = point.y;

      if (pointers.current.size >= 2 && pinchOrigin.current) {
        const [a, b] = [...pointers.current.values()];
        if (!a || !b) return;
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const centre = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const origin = pinchOrigin.current;
        if (origin.distance <= 0) return;

        const scale = clampScale(origin.viewport.scale * (distance / origin.distance));
        const ratio = scale / origin.viewport.scale;
        // Pinch zooms about the gesture centre and pans with it in one step.
        setViewportState({
          scale,
          x: centre.x - (origin.centre.x - origin.viewport.x) * ratio,
          y: centre.y - (origin.centre.y - origin.viewport.y) * ratio,
        });
        return;
      }

      const origin = panOrigin.current;
      if (!origin) return;
      setViewportState({
        scale: origin.viewport.scale,
        x: origin.viewport.x + (point.x - origin.x),
        y: origin.viewport.y + (point.y - origin.y),
      });
    },
    [localPoint],
  );

  const endPointer = useCallback((event: ReactPointerEvent<Element>) => {
    pointers.current.delete(event.pointerId);
    capturePointer(event.currentTarget as Element, event.pointerId, false);

    if (pointers.current.size === 1) {
      // Lifting one finger of a pinch continues as a pan from where it is now.
      const remaining = [...pointers.current.values()][0]!;
      panOrigin.current = { x: remaining.x, y: remaining.y, viewport: viewportRef.current };
      pinchOrigin.current = null;
      return;
    }
    if (pointers.current.size === 0) {
      panOrigin.current = null;
      pinchOrigin.current = null;
      setIsPanning(false);
    }
  }, []);

  const onWheel = useCallback(
    (event: ReactWheelEvent<Element>) => {
      const point = localPoint(event);
      if (event.ctrlKey || event.metaKey) {
        // Trackpad pinch, or Ctrl+wheel on a mouse.
        zoomAbout(Math.exp(-event.deltaY * 0.01), point);
        return;
      }
      const current = viewportRef.current;
      setViewportState({
        scale: current.scale,
        x: current.x - event.deltaX,
        y: current.y - event.deltaY,
      });
    },
    [localPoint, zoomAbout],
  );

  /* --- native wheel listener ------------------------------------------ */

  // React attaches wheel handlers passively, so `preventDefault` inside the
  // React handler is ignored and the page scrolls behind the diagram. A
  // non-passive native listener is the only way to stop that.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const block = (event: WheelEvent): void => {
      event.preventDefault();
    };
    element.addEventListener('wheel', block, { passive: false });
    return () => element.removeEventListener('wheel', block);
  }, [containerRef]);

  const handlers = useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp: endPointer,
      onPointerCancel: endPointer,
      onWheel,
    }),
    [onPointerDown, onPointerMove, endPointer, onWheel],
  );

  return {
    viewport,
    isPanning,
    setViewport,
    zoomBy,
    zoomIn,
    zoomOut,
    resetZoom,
    fitToScreen,
    centerOn,
    revealRect,
    toGraphPoint,
    handlers,
  };
}

/** The graph-space rectangle currently visible, for culling. */
export function visibleRect(viewport: Viewport, width: number, height: number, margin = 200): Rect {
  return {
    x: (-viewport.x - margin) / viewport.scale,
    y: (-viewport.y - margin) / viewport.scale,
    width: (width + margin * 2) / viewport.scale,
    height: (height + margin * 2) / viewport.scale,
  };
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}
